import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowLeft, Linkedin, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatDate } from "../lib/api";
import { MaskLines, Eyebrow } from "../components/site/Reveal";
import { CTA } from "../components/site/CTA";

const isHeading = (line) => {
  if (/^#{2,3}\s+/.test(line)) return true;
  if (/^[-•*]/.test(line)) return false;
  if (/[?.!]$/.test(line)) return line.length <= 90;
  if (/[.:;,]$/.test(line)) return false;
  return line.length <= 60 && /^[A-Z0-9"'(]/.test(line) && line.split(" ").length >= 2;
};

const isListItem = (line) => /^([•*▪·-]\s*|\d+[.)]\s+)/.test(line);

const inline = (text, keyBase) => {
  const parts = [];
  let rest = text;
  let k = 0;
  const pattern = /(\*\*[^*]+\*\*)|(\[[^\]]+\]\([^)]+\))/;
  while (rest) {
    const m = rest.match(pattern);
    if (!m) { parts.push(rest); break; }
    if (m.index > 0) parts.push(rest.slice(0, m.index));
    const token = m[0];
    if (token.startsWith("**")) parts.push(<strong key={`${keyBase}-${k}`}>{token.slice(2, -2)}</strong>);
    else {
      const label = token.slice(1, token.indexOf("]"));
      const href = token.slice(token.indexOf("(") + 1, -1);
      parts.push(<a key={`${keyBase}-${k}`} href={href} target="_blank" rel="noreferrer">{label}</a>);
    }
    rest = rest.slice(m.index + token.length);
    k += 1;
  }
  return parts;
};

const Body = ({ text }) => {
  const blocks = [];
  let list = [];
  const flush = () => {
    if (list.length) blocks.push(<ul key={`u${blocks.length}`} data-testid="blog-list">{list.map((l, i) => <li key={i}>{inline(l, `u${blocks.length}-${i}`)}</li>)}</ul>);
    list = [];
  };
  (text || "").replace(/\r/g, "").split("\n").forEach((raw, idx) => {
    let line = raw.trim().replace(/^•\s*/, (mm) => (mm ? "- " : ""));
    if (isListItem(raw.trim()) && !line.startsWith("## ")) {
      line = line.replace(/^-\s*/, "");
      list.push(line);
      return;
    }
    flush();
    if (!line) return;
    if (/^###\s+/.test(line)) { blocks.push(<h2 key={idx} className="h3">{line.replace(/^###\s+/, "")}</h2>); return; }
    if (/^##\s+/.test(line) || isHeading(line)) { blocks.push(<h2 key={idx}>{line.replace(/^##\s+/, "")}</h2>); return; }
    if (line.length <= 90 && line.endsWith(":")) {
      blocks.push(<p key={idx} className="jk-lead">{inline(line, `p${idx}`)}</p>);
      return;
    }
    blocks.push(<p key={idx}>{inline(line, `p${idx}`)}</p>);
  });
  flush();
  return <div data-testid="blog-content" className="prose-jk text-base sm:text-lg">{blocks}</div>;
};

export default function BlogPost() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [error, setError] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 25 });

  useEffect(() => {
    setBlog(null);
    setError(false);
    api.get(`/blogs/${slug}`).then((r) => setBlog(r.data)).catch(() => setError(true));
  }, [slug]);

  const copy = () => {
    navigator.clipboard?.writeText(window.location.href);
    toast.success("Link copied");
  };

  if (error) {
    return (
      <section className="bg-paper text-ink min-h-[70vh] grid place-items-center px-5 pt-24">
        <div className="text-center">
          <p className="font-serif-i text-4xl">Article not found.</p>
          <Link to="/blogs" className="mt-6 inline-block text-[var(--brass-deep)] underline">Back to insights</Link>
        </div>
      </section>
    );
  }
  if (!blog) return <section className="bg-paper min-h-screen" />;

  return (
    <>
      <motion.div style={{ scaleX: progress }} className="fixed top-0 inset-x-0 h-[3px] bg-[var(--brass)] origin-left z-[60]" />
      <article data-testid="blog-post">
        <header className="bg-paper text-ink pt-36 sm:pt-44 pb-16 relative overflow-hidden">
          <div className="pointer-events-none absolute -right-40 -top-40 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(29,63,168,0.08),transparent_65%)]" />
          <div className="relative mx-auto max-w-4xl px-5 sm:px-8">
            <Link to="/blogs" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-sm text-ink/70 hover:text-[var(--brass-deep)] transition-colors"><ArrowLeft className="w-4 h-4" /> All insights</Link>
            <div className="mt-10"><Eyebrow>{blog.category}</Eyebrow></div>
            <h1 data-testid="blog-title" className="mt-6 font-display font-extrabold tracking-[-0.03em] leading-[1.02] text-4xl sm:text-5xl lg:text-6xl">
              <MaskLines lines={[blog.title]} />
            </h1>
            <p className="mt-8 font-mono-x text-xs uppercase tracking-[0.18em] text-ink/70">{formatDate(blog.created_at)} · {blog.read_time} min read · Jay Kular</p>
          </div>
        </header>
        {blog.cover_image && (
          <div className="bg-paper">
            <div className="mx-auto max-w-5xl px-5 sm:px-8">
              <img src={blog.cover_image} alt={blog.title} className="w-full aspect-[16/8] object-cover rounded-t-[24px] border border-ink/10 border-b-0" />
            </div>
          </div>
        )}
        <div className="bg-paper py-16 sm:py-24">
          <div className="mx-auto max-w-3xl px-5 sm:px-8">
            {blog.excerpt && (
              <div className="mb-12 rounded-2xl bg-[#f5f7fa] border-l-4 border-[var(--brass)] p-6 sm:p-8">
                <p data-testid="blog-excerpt" className="font-serif-i text-xl sm:text-2xl leading-snug text-ink">{blog.excerpt}</p>
              </div>
            )}
            <Body text={blog.content} />
            <div className="mt-16 pt-8 border-t border-ink/15 flex items-center gap-3">
              <span className="text-sm text-ink/65 mr-2">Share</span>
              <a data-testid="share-linkedin" target="_blank" rel="noreferrer" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`} className="grid place-items-center w-10 h-10 rounded-full border border-ink/25 hover:bg-ink hover:text-paper transition-colors"><Linkedin className="w-4 h-4" /></a>
              <button data-testid="share-copy-link" onClick={copy} className="grid place-items-center w-10 h-10 rounded-full border border-ink/25 hover:bg-ink hover:text-paper transition-colors"><Link2 className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      </article>
      <CTA />
    </>
  );
}
